type SuccessResponse<T> = {
  success: true,
  data?: T,
  redirectUrl?: string,
  errors?: string[],
}

type ErrorResponse = {
  success: false,
  data?: object,
  redirectUrl?: string,
  errors: string[],
}

type ActionResponse<T> =
  | Promise<SuccessResponse<T> | ErrorResponse>
  | SuccessResponse<T>
  | ErrorResponse

/**
 * E = EntityParams Type (e.g., AgencyModel)
 * S = SuccessData Type (e.g., AgencyModel)
 */
export interface HandleCallbackActionParams<E = object, S = object> {
  callbacks: {
    action: (...args: [E] | [E, object | undefined]) => ActionResponse<S>,

    buildActionParams?: (entityParams?: E) => [E] | [E, object | undefined],

    afterAction?: (params: { response: SuccessResponse<S> }) => void,
    afterError?: (params: { response: ErrorResponse }) => void,
    alertError?: (params: { message: string, response: ErrorResponse }) => void,
    alertSuccess?: (params: { response: SuccessResponse<S> }) => void,
  },
  entityParams?: E,
  requestOptions?: object,
  shouldRedirect?: boolean,
}

/**
 * Utility function to handle a generic asynchronous action and manage callbacks/alerts.
 * @param params The parameters defining the action and callbacks.
 * @returns The action response (SuccessResponse or ErrorResponse).
 */
const handleCallbackAction = async <E = object, S = object>(
  params: HandleCallbackActionParams<E, S>,
): Promise<SuccessResponse<S> | ErrorResponse> => {
  const {
    callbacks: {
      action,
      afterAction,
      afterError,
      alertError,
      alertSuccess,
      buildActionParams,
    },
    entityParams,
    requestOptions,
    shouldRedirect,
  } = params

  const requestParams: [E] | [E, object | undefined] = buildActionParams
    ? buildActionParams(entityParams!)
    : [entityParams!, requestOptions]

  const response = await action(...requestParams) as SuccessResponse<S> | ErrorResponse

  const { success } = response || {}

  if (!success){
    const errorResponse = response as ErrorResponse

    if (alertError) alertError({ message: errorResponse.errors[0] || 'Unknown Error', response: errorResponse })
    if (afterError) afterError({ response: errorResponse })

    return errorResponse
  }

  const successResponse = response as SuccessResponse<S>

  if (alertSuccess) alertSuccess({ response: successResponse })

  if (shouldRedirect && successResponse.redirectUrl){
    if (typeof window !== 'undefined'){
      window.location.href = successResponse.redirectUrl
    }
  }

  if (afterAction) afterAction({ response: successResponse })

  return successResponse
}

export default handleCallbackAction
